import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import { leadService } from "../../services/leadService";
import { campaignService } from "../../services/campaignService";
import { useToast } from "../ui/use-toast";
import { Card, CardContent } from "../ui/card";
import { AlertCircle } from "lucide-react";
import { featureTemplateService } from "@/services/featureTemplateService";

interface LeadSelectorProps {
  campaignId: string;
  organizationId: string;
  onComplete: () => void;
}

export function LeadSelector({
  campaignId,
  organizationId,
  onComplete,
}: LeadSelectorProps) {
  const [leads, setLeads] = useState<any[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [addingLeads, setAddingLeads] = useState(false);
  const [currentLeadCount, setCurrentLeadCount] = useState(0);
  const [maxLeadsLimit, setMaxLeadsLimit] = useState<number | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get campaign details to get the feature template
        const { data: campaign } = await campaignService.getCampaign(campaignId);
        if (!campaign) throw new Error("Campaign not found");

        // Get feature template to check limits
        const featureTemplate = await featureTemplateService.getFeatureTemplate(
          campaign.feature_template_id
        );
        if (featureTemplate?.max_leads_per_instance) {
          setMaxLeadsLimit(featureTemplate.max_leads_per_instance);
        }

        // Get current lead count
        const { data: campaignLeads } = await campaignService.getCampaignLeads(campaignId);
        setCurrentLeadCount(campaignLeads?.length || 0);

        // Get available leads
        const { data, error } = await leadService.getLeads(organizationId);
        if (error) throw error;
        if (data) {
          setLeads(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to load leads",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId, campaignId]);

  const handleSelectLead = (leadId: string) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId],
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map((lead) => lead.id));
    }
  };

  const handleAddLeads = async () => {
    if (selectedLeads.length === 0) {
      toast({
        title: "No leads selected",
        description: "Please select at least one lead to add",
        variant: "destructive",
      });
      return;
    }

    // Check if adding these leads would exceed the limit
    if (maxLeadsLimit !== null) {
      const newTotal = currentLeadCount + selectedLeads.length;
      if (newTotal > maxLeadsLimit) {
        toast({
          title: "Limit Exceeded",
          description: `Adding these leads would exceed the maximum limit of ${maxLeadsLimit} leads for this campaign.`,
          variant: "destructive",
        });
        return;
      }
    }

    setAddingLeads(true);
    try {
      const { success, error } = await campaignService.addLeadsToCampaign(
        campaignId,
        selectedLeads,
      );

      if (error) throw error;

      toast({
        title: "Leads added",
        description: `${selectedLeads.length} leads added to campaign`,
      });

      onComplete();
    } catch (error) {
      console.error("Error adding leads to campaign:", error);
      toast({
        title: "Error",
        description: "Failed to add leads to campaign",
        variant: "destructive",
      });
    } finally {
      setAddingLeads(false);
    }
  };

  const filteredLeads = leads.filter(
    (lead) =>
      lead.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone_number?.includes(searchQuery),
  );

  return (
    <div className="space-y-4">
      {maxLeadsLimit !== null && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-500" />
            <p className="text-blue-800">
              This campaign is limited to {maxLeadsLimit} leads. Currently using {currentLeadCount} leads.
              {currentLeadCount >= maxLeadsLimit ? (
                " You cannot add more leads."
              ) : (
                ` You can add up to ${maxLeadsLimit - currentLeadCount} more leads.`
              )}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="search">Search Leads</Label>
          <div className="flex w-full max-w-sm items-center space-x-2">
            <Input
              id="search"
              placeholder="Search by name, email, or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {selectedLeads.length} selected
          </span>
          <Button
            onClick={handleAddLeads}
            disabled={selectedLeads.length === 0 || addingLeads}
          >
            {addingLeads ? "Adding..." : "Add Selected Leads"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center">Loading leads...</div>
      ) : filteredLeads.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-muted-foreground">
            No leads found matching your search
          </p>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={
                      selectedLeads.length > 0 &&
                      selectedLeads.length === filteredLeads.length
                    }
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all leads"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={() => handleSelectLead(lead.id)}
                      aria-label={`Select ${lead.first_name} ${lead.last_name}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {lead.first_name} {lead.last_name}
                  </TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>{lead.phone_number}</TableCell>
                  <TableCell>{lead.source || "Manual"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
